/**
 * Enterprise Navigation Overhaul — the single source of truth every
 * nav-aware surface reads from: `layouts/AppLayout/Sidebar.tsx`
 * (expanded/collapsed/accordion + favorites + recents + dynamic
 * badges), `layouts/AppLayout/Breadcrumbs.tsx` (registry-derived
 * trail), and `features/dashboard/hooks/useGlobalSearch.ts` (Ctrl+K
 * navigation results). Supersedes the old flat `nav-config.ts`.
 *
 * Every leaf in the user's proposed 11-section tree gets a real entry
 * here — where a real page/tab already exists, `path` points straight
 * at it (adding `?tab=...` for hub-page tabs, so every leaf is still a
 * distinct, deep-linkable, breadcrumb-addressable URL even though many
 * of them share one physical route/component).
 *
 * EVERY LEAF HERE LEADS SOMEWHERE REAL. The registry used to carry 62
 * `COMING_SOON` placeholders that routed to a shared "coming soon" page;
 * they were removed on 2026-08-19, along with the three top-level sections
 * (Insurance, Materials, Human Resources) and the Dashboard section that
 * consisted of nothing else. A row that opens onto a placeholder costs an
 * operator the same click as a real one and teaches them the sidebar lies.
 * If a module is not built, it does not get a row.
 */
import type { SvgIconComponent } from '@mui/icons-material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import PaymentsIcon from '@mui/icons-material/Payments';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import MedicationIcon from '@mui/icons-material/Medication';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import BiotechIcon from '@mui/icons-material/Biotech';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import BusinessIcon from '@mui/icons-material/Business';
import RuleIcon from '@mui/icons-material/Rule';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import InsightsIcon from '@mui/icons-material/Insights';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import TuneIcon from '@mui/icons-material/Tune';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CategoryIcon from '@mui/icons-material/Category';
import SettingsIcon from '@mui/icons-material/Settings';
import StorefrontIcon from '@mui/icons-material/Storefront';
import BadgeIcon from '@mui/icons-material/Badge';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
// DISCHARGE MODULE (owner, 2026-10-23) — the sidebar glyphs are the shell's own (MUI); the pages they open use none.
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import { MODIFICATIONS_MODULE } from '@core/permissions/modifications-access';
import { DOWNLOAD_MODULE } from '@core/permissions/download-access';
import { DOWNLOAD_AREAS, DOWNLOAD_PATH, type DownloadAreaId } from './download-areas';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
  FINANCE_PAGES,
  FINANCE_ROUTE_PERMISSION,
  financePageNeeds,
  financePageRowId,
  financeTabEntryId,
  financeTabUrl,
} from '@features/finance/odoo/finance-menu';
import SchoolIcon from '@mui/icons-material/School';
import {
  PatientPermissions,
  AppointmentPermissions,
  FinancialPermissions,
  FinanceAccountingPermissions,
  MasterDataPermissions,
  ProcurementPermissions,
  InventoryPermissions,
  PharmacyBillingPermissions,
  CashManagementPermissions,
  CashierPermissions,
  type NavModuleCode,
  IpAdmissionPermissions,
  LabPermissions,
  AnalyticsPermissions,
  HrPermissions,
  PlatformPermissions,
  DischargePermissions,
  MrdPermissions,
} from '@nexao/contracts';

export type NavStatus = 'ACTIVE' | 'BETA' | 'DISABLED' | 'HIDDEN';

export interface NavRegistryItem {
  id: string;
  parentId: string | null;
  label: string;
  shortTitle?: string;
  icon?: SvgIconComponent;
  path?: string;
  requires?: string[];
  /**
   * The user needs AT LEAST ONE of these (alongside every `requires`). For a row that opens a page of several
   * sub-tabs, each with its own permission: the row is worth showing as soon as any one of them can be opened.
   * Read by `hasAccess` and `searchNavigation` — the only two places visibility is decided.
   */
  requiresAny?: string[];
  order: number;
  status: NavStatus;
  badge?: 'new' | number;
  badgeSource?: string;
  favoriteAllowed?: boolean;
  /** `false` keeps an item out of Quick Search. `true` on a HIDDEN item puts it IN, though it is not in the sidebar. */
  searchable?: boolean;
  /**
   * For an item that is not a sidebar row but belongs under one (a sub-tab of a page): the id of that row. The
   * breadcrumb trail runs through it, so the sidebar highlights the page and the header names it, while the sub-tab
   * itself stays out of the tree.
   */
  trailVia?: string;
  /** Role -> sidebar-module visibility grant this item is gated on (see `Sidebar.tsx`'s `isVisible()` and `core/permissions`' `hasModuleAccess`). Set by every `group()` call. Plain `page()` leaves rely on their own `requires` permission instead — a real endpoint always has a real permission to gate on. */
  moduleCode?: NavModuleCode | typeof MODIFICATIONS_MODULE | typeof DOWNLOAD_MODULE;
}

/**
 * The five families (ADR-061). A section carries one as its IDENTITY, so a
 * clerk learns "Pharmacy is the green one" the way they learn its position.
 *
 * Five, not one per section: the palette stops meaning anything the moment a
 * sixth colour is added, so eight sections share five hues rather than
 * inventing three more.
 */
export type NavHue = 'blue' | 'red' | 'yellow' | 'green' | 'grey';

/**
 * Section identity, by top-level id. Children inherit from their section —
 * every page under Pharmacy is green, so the sidebar row and the page it opens
 * agree.
 *
 * Anything unlisted falls back to grey, which is the neutral family and the
 * honest answer for "no identity assigned".
 */
export const SECTION_HUE: Record<string, NavHue> = {
  'front-office': 'blue',
  clinical: 'red',
  'diagnostics-lab': 'yellow',
  pharmacy: 'green',
  billing: 'blue',
  /* Green for cash in hand — the section is about custody, not about the
     ledgers Finance keeps in blue. */
  'main-counter': 'green',
  finance: 'green',
  /* Blue, with Billing: insurance is billed money owed by a third party, and it
     must not borrow Clinical's red. */
  insurance: 'blue',
  reports: 'yellow',
  administration: 'grey',
  /* Blue, with Billing and Insurance: what is being modified is money already taken, and the section is a desk's, not the hospital's. */
  modifications: 'blue',
  /* Grey, with Administration: it is the administrator's own tool and carries no clinical or money identity. */
  download: 'grey',
  /* The ward's own colour, distinct from Front Desk's blue: the two sections
     share screens and must not share an identity. */
  inpatient: 'red',
  /* Green — the way out. Discharge shares screens and patients with the ward (red) but is a different desk, with a different
     job and different people, so it must not share the ward's identity. */
  discharge: 'green',
  /* Blue — the records room. Not Discharge's green and not the ward's red: it is a desk of its own, reading what the others wrote. */
  mrd: 'blue',
  radiology: 'yellow',
  /* Grey: General Stores is a supply function, not a clinical or money one.
     Deliberately NOT pharmacy's green — a storekeeper must never mistake the
     stores stock screen for the pharmacy's, since only one of them may be
     dispensed from. */
  'general-stores': 'grey',
  /* Green: people and wellbeing, distinct from Admin's neutral grey and from
     the default accent (blue) every panel renders unless told otherwise —
     so HR's own screens read as HR's own, not as an unlabelled default. */
  'human-resources': 'green',
};

/** The hue an item inherits — its own section's, walking up to the top level. */
export function hueOf(itemId: string): NavHue {
  let current = navigationRegistry.find((item) => item.id === itemId);
  while (current?.parentId) current = navigationRegistry.find((item) => item.id === current?.parentId);
  return (current && SECTION_HUE[current.id]) || 'grey';
}

export const navigationRegistryVersion = 1;

let n = 0;
const next = (): number => (n += 1) * 10;

/** One icon per Finance page row — the flat workspace rail shows every row with one. */
const FINANCE_PAGE_ICONS: Record<string, SvgIconComponent> = {
  receivables: ReceiptLongIcon,
  payables: ShoppingCartIcon,
  accounting: AccountBalanceIcon,
  'cash-bank': PaymentsIcon,
  banking: SwapHorizIcon,
  reporting: AssessmentIcon,
  setup: SettingsIcon,
};

function group(
  id: string,
  parentId: string | null,
  label: string,
  moduleCode: NavModuleCode | typeof MODIFICATIONS_MODULE | typeof DOWNLOAD_MODULE,
  icon?: SvgIconComponent,
  requires?: string[],
  path?: string,
): NavRegistryItem {
  return { id, parentId, label, icon, requires, path, order: next(), status: 'ACTIVE', favoriteAllowed: false, searchable: false, moduleCode };
}

function page(
  id: string,
  parentId: string,
  label: string,
  path: string,
  opts: {
    requires?: string[];
    requiresAny?: string[];
    status?: NavStatus;
    icon?: SvgIconComponent;
    badgeSource?: string;
    searchable?: boolean;
    trailVia?: string;
  } = {},
): NavRegistryItem {
  return {
    id,
    parentId,
    label,
    path,
    icon: opts.icon,
    requires: opts.requires,
    ...(opts.requiresAny ? { requiresAny: opts.requiresAny } : {}),
    order: next(),
    status: opts.status ?? 'ACTIVE',
    badgeSource: opts.badgeSource,
    ...(opts.searchable !== undefined ? { searchable: opts.searchable } : {}),
    ...(opts.trailVia ? { trailVia: opts.trailVia } : {}),
  };
}

const DOWNLOAD_AREA_ICON: Record<DownloadAreaId, SvgIconComponent> = {
  op: HowToRegIcon,
  ip: MeetingRoomIcon,
  lab: BiotechIcon,
  pharmacy: MedicationIcon,
  finance: AccountBalanceIcon,
  patients: PersonSearchIcon,
  hr: BadgeIcon,
  masters: CategoryIcon,
  audit: FactCheckIcon,
};

export const navigationRegistry: NavRegistryItem[] = [

  // ── Front Office ───────────────────────────────────────────────────
  /*
    FRONT OFFICE IS THE FRONT DESK WORKSTATION'S OWN RAIL — desk, 2026-09-04:
    "see how this frontdesk sidebar is showing only this options should show
    under administrator frontdesk".

    An administrator switching to the Front Desk workspace sees SIX rows, tuned
    against live feedback (`WORKSPACE_FLAT_NAV['front-desk']`). In the Admin
    workspace the same section showed nine, and most of the extra rows were the
    same screen reached another way:

      - `Front Desk Workstation`, `Patient Search` and `Patient Merge` are the
        workstation and two of its own TABS. Registration below opens that
        workstation, so its tab strip carries Search and Merge one click away —
        nothing became unreachable, three rows for one screen went.
      - `OPD Check-in` is a tab on the appointment workstation, which the row
        above it opens.
      - `IP Dashboard` and `IP Lookup` moved to the Inpatient section, which
        mirrors the IP workstation's own rail.

    SO THIS LIST IS NOW THE RAIL, ROW FOR ROW AND IN ITS ORDER — register, book,
    admit, then bill by department. The desk's order is a workflow, and the
    administrator should see the desk's world the way the desk sees it.

    THE THREE BILLING ROWS ARE SHARED WITH THE BILLING SECTION, deliberately and
    by the pattern this file already uses for its workspace rails: "a workspace
    decides what is OFFERED together, not what exists" (see `WORKSPACE_FLAT_NAV`
    below). The counter screens are the front desk's daily work AND the billing
    module's own; neither section is a wrong home. They are ids reused, not
    entries moved, so `requires` and paths stay single-sourced.
  */
  group('front-office', null, 'Front Desk', 'FRONT_OFFICE', PeopleIcon),
  /*
    THE DESK'S DASHBOARD, AND IT IS FIRST — 2026-09-07.

    There WAS a dashboard here, and it was a tab of the registration
    workstation: `/patients/workstation?tab=dashboard`. Two things were wrong
    with that and the desk said both. It was filed under "New Patient
    Registration", which is one of the desk's jobs rather than all of them, so
    nobody looking for the desk's own numbers thought to open the registration
    screen. And its panels counted THIS BROWSER SESSION — "0 registered",
    "no queue read endpoint exists" — rather than the hospital, so it reset on
    every reload.

    This row replaces it with a real server-side report, and it sits first
    because a dashboard that is not the first thing in its own section is a
    dashboard nobody opens. The old tab is gone from the workstation entirely,
    so there is exactly one Front Desk dashboard.

    Gated on `analytics:report:view`, NOT on a patient permission — it reads the
    `/analytics` surface, and gating a row on a permission its endpoint does not
    check is how a leaf becomes visible to a role that gets a 403 on click.
    A Front Desk role therefore needs the grant: `scripts/grant-analytics-reports.ts`.
  */
  page('fo-dashboard', 'front-office', 'Dashboard', '/front-desk/dashboard', {
    icon: InsightsIcon,
    requires: [AnalyticsPermissions.ANALYTICS_REPORT_VIEW],
  }),
  /*
    THE NEW REGISTRATION PAGE, NOT THE OLD WORKSTATION SHELL — 2026-09-07.

    This row pointed at `/patients/workstation?tab=registration` from
    2026-08-02 (`d4135ad`, when the rail was narrowed to six screens). Meanwhile
    `/patients/register` — `PatientRegistrationPage` — kept being built up
    through 2026-08-20, 08-30 and 09-05, gaining a Dashboard, the PATIENT
    REGISTER (`PatientListTab`) and Config. NO SIDEBAR LEAF EVER POINTED AT IT.

    So for five weeks the desk opened the older shell, whose middle tab is the
    MUI/AG-Grid `PatientSearchPage`, and the newer Patient Register was
    unreachable except by typing the URL. That is exactly what was reported
    ("patient register page why not showing? patient search new page should show
    why old one showing?") — not a regression, a row that was never moved.

    `?tab=register` opens on the registration FORM, which is what this row's
    label promises and what the old target opened on. The Dashboard and Patient
    Register sit beside it on the same page.

    MERGE WAS REMOVED ENTIRELY on the same day (desk: "merge not required remove
    this page"), so nothing was left behind by the move — the page, its route and
    the button on the search grid are all gone.

    `/patients/workstation` STAYS ROUTED. Old bookmarks and the F2/F8 commands
    keep working; this changes what the sidebar OFFERS, not what exists — the
    same distinction this file keeps for `/billing/counter`.
  */
  page('fo-patient-registration', 'front-office', 'New Patient Registration', '/patients/register?tab=register', { icon: HowToRegIcon, requires: [PatientPermissions.PATIENT_REGISTER] }),
  // Named for BOTH things the screen does. A returning patient is booked from
  // this same workstation — the revisit fields live on the booking — and a rail
  // that says only "Appointments" sent the desk hunting for a separate
  // "Repeat Patient" screen that has never existed.
  page('fo-appointments', 'front-office', 'Repeat Patient / Appointment', '/appointments', { icon: EventAvailableIcon, requires: [AppointmentPermissions.APPOINTMENT_BOOK] }),
  // Admitting sits between booking and billing because that is the order it
  // happens in, not because it is a billing screen.
  page('fo-ip-admission', 'front-office', 'IP Admission', '/ip/register', { icon: MeetingRoomIcon, requires: [IpAdmissionPermissions.IP_ADMISSION_ADMIT],
  }),
  page('fo-cash-counter', 'front-office', 'Cash Counter', '/op-billing', { icon: PaymentsIcon, requires: [FinancialPermissions.FINANCIAL_PAYMENT_PROCESS] }),
  page('fo-lab-billing', 'front-office', 'Laboratory Billing', '/billing/laboratory', { icon: BiotechIcon, requires: [FinancialPermissions.FINANCIAL_PAYMENT_PROCESS] }),
  page('fo-radiology-billing', 'front-office', 'Radiology Billing', '/billing/radiology', { icon: CameraAltIcon, requires: [FinancialPermissions.FINANCIAL_PAYMENT_PROCESS] }),

  /*
    ── Inpatient ──────────────────────────────────────────────────────
    THE IP WORKSTATION'S RAIL, for the same reason as Front Office above: the
    administrator should see the ward's world the way the ward does.

    `WORKSPACE_FLAT_NAV.ip` is dashboard -> lookup -> admit -> bill, in the
    order a stay moves. Admitting is not repeated here — it is the front desk's
    row, listed above, and one page twice in one tree is what this round set out
    to remove.
  */
  /*
    `FRONT_OFFICE`, NOT a module of its own — and this line was `'IP'` for an
    hour, which is not a `NavModuleCode` at all.

    It typechecked: `NavModuleCode` resolves to `any` through this workspace's
    `@nexao/contracts` alias, so the union does not constrain these calls (a
    probe assigning a nonsense string compiles clean). `moduleCode` is what
    `Sidebar.tsx` gates a section on, so an invalid one is not a typo — it is a
    section NO ROLE CAN SEE, silently. `navigation-registry.integrity.test.ts`
    now checks every code against `NAV_MODULE_CODES` at runtime, because the
    type cannot.

    FRONT_OFFICE was the right answer AT THE TIME rather than merely a valid
    one: these three leaves lived under Front Office until that day, so every
    role that could already reach them still could. A new module code would have
    hidden the section from everyone until roles were re-provisioned.

    ── AND IT IS `CLINICAL_IPD` NOW (desk, 2026-09-08) ────────────────────────

    That reuse made ONE code do TWO unrelated jobs: "may see the IP screens" and
    "gets the Front Desk workstation", which is what `WORKSPACE_MODULE_CODE`
    keys `front-desk` on. They could not be granted apart, so every IP Officer
    silently received the whole Front Desk workstation — user 2212 (SHOBHA M)
    holds IP Officer and Main Cash Counter, has never been ticked Front Desk,
    and opened on it anyway.

    The desk's rule for this screen is exactly that: "under assign roles what
    ever ticked that should come... no special rules". A role granting a
    workstation nobody ticked is precisely the special rule they are refusing.

    THE RE-PROVISIONING THE OLD NOTE FEARED IS ALREADY DONE — `CLINICAL_IPD` was
    granted to IP Officer on 2026-09-08, so this hides the section from nobody
    who should see it. And the FRONT DESK RAIL IS UNAFFECTED: its own IP row is
    `fo-ip-admission`, parented to `front-office` and gated on FRONT_OFFICE plus
    `ip:admission:admit` — the desk admits patients but deliberately does not
    run the inpatient bill, so none of THIS group's leaves are on its rail.
  */
  group('inpatient', null, 'IP', 'CLINICAL_IPD', MeetingRoomIcon),
  // The state of the ward, asked before "what shall I do with one stay".
  page('ip-dashboard', 'inpatient', 'IP Dashboard', '/ip/dashboard', { icon: InsightsIcon, requires: [IpAdmissionPermissions.IP_DASHBOARD_VIEW],
  }),
  /*
   * The read-only stay reference, carrying only `ip:admission:view` so the
   * roles that may look a stay up but never admit anyone — ward clerks,
   * supervisors, the enquiry counter — get a row they can actually use.
   */
  page('ip-lookup', 'inpatient', 'IP Lookup', '/ip/lookup', { icon: SearchIcon, requires: [IpAdmissionPermissions.IP_ADMISSION_VIEW],
  }),
  page('ip-billing', 'inpatient', 'IP Billing', '/ip-billing', { icon: PaymentsIcon, requires: [IpAdmissionPermissions.IP_ADMISSION_VIEW],
  }),
  /*
   * Pharmacy bills entered against ADMITTED stays, with refunds and a register
   * (desk, 2026-09-25). Gated on `ip:charge:view`: the page only reads and writes
   * charges, and the two acts inside it carry their own capture/reverse grants.
   */
  page('ip-drug-entries', 'inpatient', 'IP Drug Entries', '/ip/drug-entries', { icon: MedicationIcon, requires: [IpAdmissionPermissions.IP_CHARGE_VIEW],
  }),

  /*
    ── Discharge ──────────────────────────────────────────────────────
    DISCHARGE MODULE (owner, 2026-10-23: "need Discharge Module under sidebar it should follow the role based as like other modules").

    Its own section and its own module code, granted in Administration → Roles → Modules like every other: the discharge coordinators,
    the insurance desk and the records room need this workspace without the IP billing desk, and the other way round. Each page then
    carries its own permission, so holding the module opens the section and the permission opens the page.

    The rail runs in the order a discharge is worked: the state of the hospital, the queue, one stay, the lab file, the register.
  */
  group('discharge', null, 'Discharge', 'DISCHARGE', ExitToAppIcon),
  page('discharge-dashboard', 'discharge', 'Dashboard', '/discharge/dashboard', { icon: InsightsIcon, requires: [DischargePermissions.DISCHARGE_DESK_VIEW] }),
  page('discharge-worklist', 'discharge', 'Worklist', '/discharge/worklist', { icon: PendingActionsIcon, requires: [DischargePermissions.DISCHARGE_DESK_VIEW] }),
  page('discharge-desk', 'discharge', 'Discharge Desk', '/discharge/desk', { icon: HowToRegIcon, requires: [DischargePermissions.DISCHARGE_DESK_VIEW] }),
  page('discharge-lab', 'discharge', 'Lab Reports', '/discharge/lab-reports', { icon: BiotechIcon, requires: [DischargePermissions.DISCHARGE_LAB_REPORT_VIEW] }),
  page('discharge-register', 'discharge', 'Register', '/discharge/register', { icon: FactCheckIcon, requires: [DischargePermissions.DISCHARGE_DESK_VIEW] }),

  /*
    ── MRD ───────────────────────────────────────────────────────────
    MRD MODULE (owner, 2026-11-02: "need new module named MRD"): the Medical Records Department.

    Its own section and its own module code (`MRD`), granted in Administration → Roles → Modules like every other. Each page carries its
    own permission, so holding the module opens the section and the permission opens the page. The sidebar glyphs are the shell's own
    (MUI); the pages they open use none.

    The rail runs in the order a file is worked: the state of the department, the files that have arrived, what is missing from them,
    the coding, the index, where the paper is, the statutory registers, the statistics, the settings.
  */
  group('mrd', null, 'MRD', 'MRD', AssignmentTurnedInIcon),
  page('mrd-dashboard', 'mrd', 'Dashboard', '/mrd/dashboard', { icon: InsightsIcon, requires: [MrdPermissions.MRD_DESK_VIEW] }),
  page('mrd-inbox', 'mrd', 'Discharged Files', '/mrd/inbox', { icon: Inventory2Icon, requires: [MrdPermissions.MRD_DESK_VIEW] }),
  page('mrd-deficiencies', 'mrd', 'Deficiencies', '/mrd/deficiencies', { icon: PendingActionsIcon, requires: [MrdPermissions.MRD_DESK_VIEW] }),
  page('mrd-coding', 'mrd', 'Coding', '/mrd/coding', { icon: RuleIcon, requires: [MrdPermissions.MRD_DESK_VIEW] }),
  page('mrd-index', 'mrd', 'Disease Index', '/mrd/index', { icon: PersonSearchIcon, requires: [MrdPermissions.MRD_DESK_VIEW] }),
  page('mrd-code-list', 'mrd', 'Code List', '/mrd/code-list', { icon: ListAltIcon, requires: [MrdPermissions.MRD_DESK_VIEW] }),
  page('mrd-file-tracker', 'mrd', 'File Tracker', '/mrd/file-tracker', { icon: SwapHorizIcon, requires: [MrdPermissions.MRD_DESK_VIEW] }),
  page('mrd-archive', 'mrd', 'Archive', '/mrd/archive', { icon: WarehouseIcon, requires: [MrdPermissions.MRD_DESK_VIEW] }),
  page('mrd-retention', 'mrd', 'Retention & Destruction', '/mrd/retention', { icon: CategoryIcon, requires: [MrdPermissions.MRD_DESK_VIEW] }),
  page('mrd-requests', 'mrd', 'Record Requests', '/mrd/requests', { icon: EventAvailableIcon, requires: [MrdPermissions.MRD_DESK_VIEW] }),
  page('mrd-certificates', 'mrd', 'Medical Certificates', '/mrd/medical-certificates', { icon: AssignmentTurnedInIcon, requires: [MrdPermissions.MRD_DESK_VIEW] }),
  page('mrd-issue-log', 'mrd', 'Issue Log', '/mrd/issue-log', { icon: ReceiptLongIcon, requires: [MrdPermissions.MRD_DESK_VIEW] }),
  page('mrd-births', 'mrd', 'Birth Register', '/mrd/births', { icon: BadgeIcon, requires: [MrdPermissions.MRD_DESK_VIEW] }),
  page('mrd-deaths', 'mrd', 'Death Register', '/mrd/deaths', { icon: HealthAndSafetyIcon, requires: [MrdPermissions.MRD_DESK_VIEW] }),
  page('mrd-mlc', 'mrd', 'MLC Register', '/mrd/mlc', { icon: VerifiedUserIcon, requires: [MrdPermissions.MRD_DESK_VIEW] }),
  page('mrd-outcomes', 'mrd', 'Outcome Register', '/mrd/outcomes', { icon: HowToRegIcon, requires: [MrdPermissions.MRD_DESK_VIEW] }),
  page('mrd-notifiable', 'mrd', 'Notifiable Diseases', '/mrd/notifiable', { icon: FactCheckIcon, requires: [MrdPermissions.MRD_DESK_VIEW] }),
  page('mrd-statistics', 'mrd', 'Statistics', '/mrd/statistics', { icon: AssessmentIcon, requires: [MrdPermissions.MRD_DESK_VIEW] }),
  page('mrd-settings', 'mrd', 'Settings', '/mrd/settings', { icon: TuneIcon, requires: [MrdPermissions.MRD_CODING_MANAGE] }),

  // ── Clinical ───────────────────────────────────────────────────────
  // No group-level `requires` — its children each carry their own,
  // different permission (CLINICAL_VIEW/CREATE/ORDER_CREATE); gating the
  // group itself on just CLINICAL_VIEW hid Consultation/Diagnosis/
  // Prescription/Orders/Documents from any role that only had
  // CLINICAL_CREATE/CLINICAL_ORDER_CREATE. Visibility now falls through
  // to `isVisible`'s own "any child visible" rule, same as the other
  // ungated groups below (`clinical-opd`, `diagnostics`, etc.).
  /*
    THE CLINICAL SECTION IS GONE FROM THE SIDEBAR — desk, 2026-09-04.

    NOTHING WAS DELETED FROM THE APPLICATION. `/clinical`, `/clinical/workspace`,
    `/clinical/orders`, `/clinical/timeline` and `/clinical/documents` are all
    still routed in `AppRouter` and still permission-gated exactly as before, so
    a bookmark or deep link keeps working. Only the sidebar rows went.

    Worth knowing before restoring them: the OPD screens are thin. There is no
    `GET /clinical/encounters`, the queue is write-only, and neither the
    consultation nor the orders page has tabs — which is why four rows collapsed
    to two earlier today. If Clinical returns it should return as the
    workstation it becomes, not as the six rows it was.
  */

  // ── Diagnostics ────────────────────────────────────────────────────
  /*
    THE "DIAGNOSTICS" WRAPPER IS GONE — desk, 2026-09-04: the admin sidebar
    should list WORKSTATIONS, and nobody works in "Diagnostics". It held exactly
    one child, Laboratory, so it was a chevron you opened to find a chevron.
    Laboratory is now top-level and keeps its own module code, path and rail.
  */
  // `path` on the group, same as Pharmacy: it is what a bare `/laboratory` or
  // `/laboratory/workflow` (no `?tab=`, e.g. an old bookmark) resolves to for
  // the breadcrumb trail. Clicking the group still only expands it — a group
  // with children never navigates.
  group('diagnostics-lab', null, 'Laboratory', 'LABORATORY', BiotechIcon, undefined, '/laboratory'),
  /**
   * ADR-087/ADR-088 shipped the LIS — the master workspace (`/laboratory`)
   * and the workflow workstation (`/laboratory/workflow`) — while these
   * leaves still pointed at `/coming-soon/*`. Each one now addresses the tab
   * that actually does that job, `?tab=` per this file's one-URL-per-leaf
   * rule.
   *
   * Two deliberate departures from the old placeholder list:
   *   - "Processing" is gone. The workstation has no such step: a specimen is
   *     received on the Specimen Worklist and resulted on the Result
   *     Worklist, and a leaf that maps onto neither is a promise the screen
   *     does not keep.
   *   - "Quality Control" has no row at all. No QC module exists (no Levey-
   *     Jennings, no control runs), and pointing it at an unrelated tab would
   *     be worse than leaving it out.
   *
   * ADR-097 — each leaf now points at its OWN ROUTE. These six were always
   * registered as six independent destinations; until the route split they all
   * resolved to one page with a different `?tab=`, so the sidebar, the
   * breadcrumb and a tab strip printed the same six names on the same screen.
   *
   * Permissions mirror what each ROUTE enforces, so a visible leaf always
   * opens. `LAB_WORKFLOW_VIEW` is the workstation's own gate (ADR-088 Phase
   * 10); Place Order additionally needs `LAB_ORDER_CREATE`, which is the one
   * action a read-only technologist must not be offered.
   */
  /*
    ── THREE LEAVES (desk, 2026-08-26) ────────────────────────────────────────
    "under lis module hide all pages just keep Dashboard, Orders and create
    Lookup page."

    HIDDEN, NOT DELETED, and the distinction is the whole design. `status:
    'HIDDEN'` removes the sidebar row (`Sidebar.isVisible`) and changes nothing
    else: every route stays declared in `AppRouter`, every permission stays as
    it was, and every link into a stage keeps working. That matters because the
    LIS is NOT all under Orders — see the note below — and the stage screens are
    still where result entry and verification happen. What changed is that the
    operator is led to them by the order in hand rather than choosing a stage
    from a list of six and then hunting for their order in it.

    THE WORK STILL REACHES ITS SCREEN. Every row of the Orders worklist carries
    a Next step control (`nextStageFor`) that opens the ORDER RECORD, and the
    record routes on to the stage its status calls for. Collection completes on
    the record itself; results and verification are their own surfaces, one
    click away, deliberately (see `OrderWorkstationInline.test.tsx`).
  */
  page('lab-dashboard', 'diagnostics-lab', 'Dashboard', '/laboratory?tab=dashboard', { requires: [LabPermissions.LAB_MASTER_VIEW] }),
  page('lab-orders', 'diagnostics-lab', 'Orders', '/laboratory/orders', { requires: [LabPermissions.LAB_WORKFLOW_VIEW] }),
  page('lab-lookup', 'diagnostics-lab', 'Lookup', '/laboratory/lookup', { requires: [LabPermissions.LAB_WORKFLOW_VIEW] }),
  /*
    OP BILLS ENQUIRE / IP BILL ENQUIRE — desk, 2026-09-25: "under LIS module
    the user unable to identify easily OP / IP patients bills related to lab
    orders. need separate two pages under sidebar."

    DIFFERENT FROM LOOKUP, DELIBERATELY. Lookup answers "what happened with
    this PATIENT's test" from an accession, UHID or name — it is a general
    search. These two answer a narrower, billing-shaped question: "what did
    this BILL raise", entered by its own number. OP Bills Enquire filters
    `/lab/orders` to one invoice (`LabOrderListQuery.invoiceNumber`); IP Bill
    Enquire resolves a bill number to its admission and reads that stay's own
    lab charges — the same data `IpBillingWorkstationPage`'s "Lab" tab
    already shows, reached here without opening an admission first.
  */
  page('lab-op-bills-enquire', 'diagnostics-lab', 'OP Bills Enquire', '/laboratory/op-bills-enquire', {
    requires: [LabPermissions.LAB_WORKFLOW_VIEW],
  }),
  page('lab-ip-bill-enquire', 'diagnostics-lab', 'IP Bill Enquire', '/laboratory/ip-bill-enquire', {
    requires: [LabPermissions.LAB_WORKFLOW_VIEW],
  }),
  /*
    LAB TESTS REPORT — desk, 2026-11-03: "this report required under lab module ... lab report should see under LIS module without financial
    amount column ... with financial amount column show only admin in LIS module itself." How many of each test were done, OP and IP, with
    search, period, filters, sort, Excel, CSV and Print. ONE ROW FOR EVERYONE in the module: the NETAMT column is decided by the SERVER from
    the signed-in person's permissions (an admin holding `analytics:report:view` gets it; the bench never receives an amount), so this row
    needs only the laboratory's own grant.
  */
  page('lab-tests-report', 'diagnostics-lab', 'Lab Tests Report', '/laboratory/tests-report', {
    requires: [LabPermissions.LAB_WORKFLOW_VIEW],
  }),
  /*
    Desk, 2026-09-17: "need separate sidebar page for billing" — a fourth
    visible leaf, reversing the earlier "three rows only" shape this group
    held (see `lis-three-leaves.test.ts`). This puts a door to billing inside
    the Laboratory module itself, so a lab user never has to leave the module
    to bill an IP order.

    ITS OWN ROUTE, `/laboratory/billing`, NOT `/billing/laboratory` (desk,
    2026-09-21: "under laboratory module there is a billing screen... not
    required OP... keep op under frontdesk... not under laboratory module").
    The two used to be the same route, so a fix aimed at one necessarily hit
    the other — OP was hidden for both, then restored for both, before the
    desk drew the actual line: a technologist billing from inside this module
    is taking IP orders and has no OP business, while Front Desk's and the
    general Billing sidebar's own `/billing/laboratory` legitimately do. Same
    page (`LabBillingPage`, `ipOnly`) and same gate — only the door and what
    it opens on differ. See `AppRouter.tsx` and `LabBillingPage.tsx`'s own
    doc comments.
  */
  page('lab-billing', 'diagnostics-lab', 'Billing', '/laboratory/billing', { icon: BiotechIcon, requires: [FinancialPermissions.FINANCIAL_PAYMENT_PROCESS] }),
  /*
    Hidden from the sidebar; reachable by route, by Next step, and by any link
    already sent to a colleague. Placing an order lost its only entry point when
    this leaf went, so the Orders screen grew a New order button in the same
    change — a hidden leaf must never mean an unreachable act.
  */
  page('lab-place-order', 'diagnostics-lab', 'Place Order', '/laboratory/orders/new', {
    requires: [LabPermissions.LAB_WORKFLOW_VIEW, LabPermissions.LAB_ORDER_CREATE],
    status: 'HIDDEN',
  }),
  page('lab-sample-collection', 'diagnostics-lab', 'Sample Collection', '/laboratory/collection', { requires: [LabPermissions.LAB_WORKFLOW_VIEW], status: 'HIDDEN' }),
  page('lab-result-entry', 'diagnostics-lab', 'Result Entry', '/laboratory/results', { requires: [LabPermissions.LAB_WORKFLOW_VIEW], status: 'HIDDEN' }),
  page('lab-verification', 'diagnostics-lab', 'Verification', '/laboratory/verification', { requires: [LabPermissions.LAB_WORKFLOW_VIEW], status: 'HIDDEN' }),
  page('lab-reports', 'diagnostics-lab', 'Reports', '/laboratory/reports', { requires: [LabPermissions.LAB_WORKFLOW_VIEW], status: 'HIDDEN' }),
  /**
   * The master workspace. Its route is gated on FINANCIAL_PAYMENT_PROCESS —
   * an audited, separately-reported defect (a lab catalogue has nothing to do
   * with processing a payment). These leaves repeat that gate deliberately:
   * the sidebar must not offer a screen the router will refuse. When the
   * route is corrected to `LAB_MASTER_VIEW`, these change with it.
   */
  /*
    THE MASTERS GROUP IS HIDDEN (desk, 2026-08-26: "hide all pages").

    Its nine leaves are configuration work — the catalogue, clinical mapping,
    specimens, containers, methods, analyzers, report templates and imports —
    done once and then rarely, by a different person than the one running the
    bench. `HIDDEN` on the GROUP is what removes the section; the leaves keep
    their own status so that un-hiding one line brings the whole thing back
    exactly as it was.

    The Lab Dashboard that used to live in here is now a top-level Laboratory
    leaf, because the desk asked for a Dashboard and this was the only one. Its
    URL is unchanged, so a bookmark still lands.

    Every one of these remains reachable by URL for whoever configures the LIS:
    the route still enforces `LAB_MASTER_VIEW`, and hiding a row is not a
    permission change.
  */
  { ...group('lab-masters', 'diagnostics-lab', 'Masters', 'LABORATORY'), status: 'HIDDEN' as NavStatus },
  page('lab-master-dashboard', 'lab-masters', 'Lab Dashboard', '/laboratory?tab=dashboard', { requires: [LabPermissions.LAB_MASTER_VIEW], status: 'HIDDEN' }),
  page('lab-master-tests', 'lab-masters', 'Tests', '/laboratory?tab=tests', { requires: [LabPermissions.LAB_MASTER_VIEW], status: 'HIDDEN' }),
  page('lab-master-test-builder', 'lab-masters', 'Test Builder', '/laboratory?tab=test-builder', { requires: [LabPermissions.LAB_MASTER_VIEW], status: 'HIDDEN' }),
  page('lab-master-mapping', 'lab-masters', 'Clinical Mapping', '/laboratory?tab=mapping', { requires: [LabPermissions.LAB_MASTER_VIEW], status: 'HIDDEN' }),
  page('lab-master-specimens', 'lab-masters', 'Specimens', '/laboratory?tab=specimens', { requires: [LabPermissions.LAB_MASTER_VIEW], status: 'HIDDEN' }),
  page('lab-master-containers', 'lab-masters', 'Containers', '/laboratory?tab=containers', { requires: [LabPermissions.LAB_MASTER_VIEW], status: 'HIDDEN' }),
  page('lab-master-methods', 'lab-masters', 'Methods', '/laboratory?tab=methods', { requires: [LabPermissions.LAB_MASTER_VIEW], status: 'HIDDEN' }),
  page('lab-master-analyzers', 'lab-masters', 'Analyzers', '/laboratory?tab=analyzers', { requires: [LabPermissions.LAB_MASTER_VIEW], status: 'HIDDEN' }),
  page('lab-master-templates', 'lab-masters', 'Report Templates', '/laboratory?tab=templates', { requires: [LabPermissions.LAB_MASTER_VIEW], status: 'HIDDEN' }),
  page('lab-master-imports', 'lab-masters', 'Imports', '/laboratory?tab=imports', { requires: [LabPermissions.LAB_MASTER_VIEW], status: 'HIDDEN' }),

  // ── Pharmacy ───────────────────────────────────────────────────────
  // Sidebar is deliberately flat here — one entry per real top-level tab
  // on the `/pharmacy` hub (`PharmacyMasterHubPage`), nothing else. No
  // sub-groups, no per-sub-master/per-sub-purchase-type entries — those
  // all live as tabs WITHIN their own tab now (Masters/Purchase/Stock
  // each nest their own further tabs), so a flat list here matches the
  // real navigable surface instead of implying pages that don't exist
  // as their own destinations anymore.
  group('pharmacy', null, 'Pharmacy', 'PHARMACY', MedicationIcon, [MasterDataPermissions.DRUG_MASTER_READ], '/pharmacy'),
  // Pharmacy Enterprise Workspaces — this tab mounts the same generic
  // `DashboardGrid` engine the hospital-wide Dashboard uses, scoped to
  // `workspace="PHARMACY"` (see `PharmacyMasterHubPage.tsx`), not a
  // second bespoke dashboard implementation.
  page('pharmacy-dashboard', 'pharmacy', 'Dashboard', '/pharmacy?tab=dashboard', { icon: DashboardIcon, requires: [MasterDataPermissions.DRUG_MASTER_READ] }),
  page('pharmacy-sales', 'pharmacy', 'Sales', '/pharmacy?tab=sales', { icon: PointOfSaleIcon, requires: [PharmacyBillingPermissions.PHARMACY_SALE_READ] }),
  // Phase 14 cutover — the real billing workstation (OP/IP/Walk-In
  // billing, Sales Register, Stock Query, Internal Transfer, Hold Bills,
  // Returns, Credit Bills, all in one page). A direct sidebar shortcut,
  // not just reachable via the Sales tab's "Create Bill" button.
  page('pharmacy-workstation', 'pharmacy', 'Workstation', '/pharmacy-billing/workstation', {
    icon: StorefrontIcon,
    requires: [PharmacyBillingPermissions.PHARMACY_SALE_READ],
  }),
  /*
   * The pharmacy enters these bills, so this is where it looks for them (desk,
   * 2026-09-30: staff 2110/2182/2215 work in the Pharmacy workspace and "have no
   * access of the IP workstation"). The SAME route and permission as the row in
   * the IP section — a second entry, not a second page — so nothing new can be
   * opened: the leaf appears only to a user holding `ip:charge:view`, inside a
   * section that already needs the PHARMACY module and `drug-master:read`.
   */
  page('pharmacy-ip-drug-entries', 'pharmacy', 'IP Drug Entries', '/ip/drug-entries', {
    icon: ReceiptLongIcon,
    requires: [IpAdmissionPermissions.IP_CHARGE_VIEW],
  }),
  page('pharmacy-purchase', 'pharmacy', 'Purchase', '/pharmacy?tab=purchase', { icon: ShoppingCartIcon, requires: [ProcurementPermissions.PROCUREMENT_PO_READ], badgeSource: 'procurement-pending-approvals' }),
  page('pharmacy-stock', 'pharmacy', 'Stock', '/pharmacy?tab=stock', { icon: Inventory2Icon, requires: [InventoryPermissions.STOCK_BATCH_READ] }),
  // Racks / Opening Stock / Stock Adjustments (desk, 2026-11-04: "unable to see in frontend"). The three are tabs of the Pharmacy Procurement &
  // Inventory hub (`/pharmacy-procurement`, `PharmacyProcurementHubPage`) and NO sidebar row opened that hub, so screens that were built and live
  // could only be reached by typing the address. Two things here are deliberate:
  //  · Each row requires what its TAB requires AND what the hub ROUTE requires (`procurement-po:read`, AppRouter's PermissionRoute): a row for a tab
  //    the user holds but a route they cannot enter would open a "no access" page. Same reasoning as `pharmacy-reports` below.
  //  · The `?tab=` values are the hub's own tab ids; navigation-registry.integrity.test.ts reads the hub's source and fails if one is renamed.
  page('pharmacy-racks', 'pharmacy', 'Racks', '/pharmacy-procurement?tab=racks', {
    icon: ViewModuleIcon,
    requires: [ProcurementPermissions.PROCUREMENT_PO_READ, InventoryPermissions.STOCK_LOCATION_READ],
  }),
  page('pharmacy-opening-stock', 'pharmacy', 'Opening Stock', '/pharmacy-procurement?tab=opening-stock', {
    icon: LibraryAddIcon,
    requires: [ProcurementPermissions.PROCUREMENT_PO_READ, InventoryPermissions.STOCK_ADJUSTMENT_READ],
  }),
  page('pharmacy-stock-adjustments', 'pharmacy', 'Stock Adjustments', '/pharmacy-procurement?tab=stock-adjustments', {
    icon: EditNoteIcon,
    requires: [ProcurementPermissions.PROCUREMENT_PO_READ, InventoryPermissions.STOCK_ADJUSTMENT_READ],
  }),
  page('pharmacy-transfers', 'pharmacy', 'Transfers', '/pharmacy?tab=transfers', { icon: SwapHorizIcon, requires: [InventoryPermissions.STOCK_TRANSFER_READ] }),
  page('pharmacy-reorder', 'pharmacy', 'Reorder', '/pharmacy?tab=reorder', { icon: TuneIcon, requires: [InventoryPermissions.REORDER_LEVEL_READ] }),
  page('pharmacy-masters', 'pharmacy', 'Masters', '/pharmacy?tab=master', { icon: CategoryIcon, requires: [MasterDataPermissions.LOOKUP_VALUE_READ] }),
  // The pharmacy's Reports are the Workstation's Reports tab (2026-11-03) — the day report, collection, bill register, exceptions and drug-wise sales,
  // built like the Main Counter's Reports. Three things here are deliberate:
  //  · `&report=day` is not decoration: `pathsMatch` treats a query path as exact-only, and the Reports hub's `rep-pharmacy` below already owns
  //    `?tab=reports` — two items on one URL tie, and the later one wins, so a click here would highlight the hub's entry instead.
  //  · It requires what the TAB requires (`revenue-analysis`) as well as the Workstation's own read: pointed at a Workstation whose Reports tab is
  //    hidden from them, a stock-only user would land on the wrong page and be left with no way to the reports they could read before.
  //  · So the older catalogue (stock, GST, dispense, purchase, expiry …) keeps its own row, on the same permission it always had.
  page('pharmacy-reports', 'pharmacy', 'Reports', '/pharmacy-billing/workstation?tab=reports&report=day', {
    icon: AssessmentIcon,
    requires: [PharmacyBillingPermissions.PHARMACY_SALE_READ, PharmacyBillingPermissions.PHARMACY_BILLING_REVENUE_ANALYSIS],
  }),
  page('pharmacy-reports-catalogue', 'pharmacy', 'More Reports', '/pharmacy?tab=reports', { icon: AssessmentIcon, requires: [MasterDataPermissions.DRUG_MASTER_READ] }),
  page('pharmacy-config', 'pharmacy', 'Config', '/pharmacy?tab=config', { icon: SettingsIcon, requires: [MasterDataPermissions.DRUG_MASTER_READ] }),

  // ── General Stores ─────────────────────────────────────────────────
  //
  // The consumables/non-drug counterpart to Pharmacy. It reuses the
  // `MATERIALS` module code, which already exists in `NAV_MODULE_CODES`
  // and survived the 2026-08-19 removal of the old placeholder Materials
  // section — the code was never the problem, the empty leaves were.
  //
  // Every leaf below leads to a real, built screen, per this file's own
  // rule. Phase 1 ships two; the inward/movement/vendor leaves join them
  // as those phases land, not before.
  //
  // PROVISIONING NOTE: `MATERIALS` is currently on SuperAdmin and System
  // Administrator only. A Store Manager role needs it granted in
  // `role_modules` or this whole section stays invisible to them however
  // many permissions they hold — the same failure the Front Desk roles hit.
  group('general-stores', null, 'General Stores', 'MATERIALS', WarehouseIcon, [InventoryPermissions.STOCK_BATCH_READ], '/general-stores'),
  page('general-stores-stock', 'general-stores', 'Stock on Hand', '/general-stores?tab=stock', {
    icon: Inventory2Icon,
    requires: [InventoryPermissions.STOCK_BATCH_READ],
  }),
  page('general-stores-inward', 'general-stores', 'Inward', '/general-stores?tab=inward', {
    icon: ShoppingCartIcon,
    requires: [ProcurementPermissions.PROCUREMENT_GRN_READ],
  }),
  page('general-stores-issues', 'general-stores', 'Issues', '/general-stores?tab=issues', {
    icon: SwapHorizIcon,
    requires: [InventoryPermissions.INVENTORY_MOVEMENT_READ],
  }),
  page('general-stores-purchasing', 'general-stores', 'Purchasing', '/general-stores?tab=purchasing', {
    icon: ReceiptLongIcon,
    requires: [ProcurementPermissions.PROCUREMENT_REQUISITION_READ],
  }),
  page('general-stores-reports', 'general-stores', 'Reports', '/general-stores?tab=reports', {
    icon: AssessmentIcon,
    requires: [InventoryPermissions.STOCK_BATCH_READ],
  }),
  page('general-stores-items', 'general-stores', 'Item Catalogue', '/general-stores?tab=items', {
    icon: CategoryIcon,
    requires: [MasterDataPermissions.ITEM_MASTER_READ],
  }),

  // ── Insurance (ADR-098) ────────────────────────────────────────────
  //
  // Phase 1 ships the two setup screens; pre-authorisation, claims and receipts
  // join as leaves when their screens exist, not before (this file's own rule:
  // every leaf leads to a real, built screen).
  //
  // PROVISIONING NOTE: the `INSURANCE` module code is on no role yet. It must be
  // granted in `role_modules` to whoever runs the insurance desk, or this whole
  // section stays invisible however many `ip:insurance:*` permissions they hold
  // — the same failure the Front Desk roles and General Stores hit.
  group('insurance', null, 'Insurance', 'INSURANCE', HealthAndSafetyIcon, [IpAdmissionPermissions.IP_INSURANCE_VIEW], '/insurance'),
  page('insurance-dashboard', 'insurance', 'Dashboard', '/insurance?tab=dashboard', {
    icon: InsightsIcon,
    requires: [IpAdmissionPermissions.IP_INSURANCE_VIEW],
  }),
  page('insurance-preauth', 'insurance', 'Pre-Auth', '/insurance?tab=pre-auth', {
    icon: FactCheckIcon,
    requires: [IpAdmissionPermissions.IP_INSURANCE_VIEW],
  }),
  page('insurance-claims', 'insurance', 'Claims', '/insurance?tab=claims', {
    icon: ReceiptLongIcon,
    requires: [IpAdmissionPermissions.IP_INSURANCE_VIEW],
  }),
  page('insurance-receipts', 'insurance', 'Receipts', '/insurance?tab=receipts', {
    icon: PaymentsIcon,
    requires: [IpAdmissionPermissions.IP_INSURANCE_VIEW],
  }),
  page('insurance-receivables', 'insurance', 'Receivables', '/insurance?tab=receivables', {
    icon: AccountBalanceIcon,
    requires: [IpAdmissionPermissions.IP_INSURANCE_VIEW],
  }),
  page('insurance-shortfalls', 'insurance', 'Shortfalls', '/insurance?tab=shortfalls', {
    icon: RuleIcon,
    requires: [IpAdmissionPermissions.IP_INSURANCE_VIEW],
  }),
  page('insurance-plans', 'insurance', 'Plans', '/insurance?tab=plans', {
    icon: VerifiedUserIcon,
    requires: [IpAdmissionPermissions.IP_INSURANCE_VIEW],
  }),
  page('insurance-insurers', 'insurance', 'Insurers & TPAs', '/insurance?tab=insurers', {
    icon: BusinessIcon,
    requires: [IpAdmissionPermissions.IP_INSURANCE_VIEW],
  }),

  // ── Billing ────────────────────────────────────────────────────────
  //
  // SPLIT FROM FINANCE (2026-08-19, desk request). Billing is what a counter
  // operator does — take money for services — and it is exactly four screens.
  // Finance is a separate section below: ledgers, vouchers and the accounts
  // behind those takings, worked by different people holding different
  // permissions. One combined "Billing & Finance" heading made a cashier scroll
  // past the general ledger to reach the counter.
  group('billing', null, 'Billing', 'BILLING', PaymentsIcon),
  // No group-level `requires` — same reasoning as 'clinical' above: its
  // children (Package Billing, Credit Notes) require permissions other
  // than FINANCIAL_PAYMENT_PROCESS, so gating the group on that one
  // permission hid them from a role that only held the others.
  // Points at the ADR-062 production workstation. `/billing` still exists and
  // still works — it is simply no longer what the sidebar leads with, so anyone
  // on a bookmark or a deep link keeps the old screen until they choose to move.
  // ADR-086 — ONE counter for every department. Front Desk carried four
  // billing entries (OP, IP, Laboratory, Radiology) for a single act: attribute
  // services to a patient and take the money. The department is a property of
  // the SERVICE, not of the screen, so it moved into the catalogue's tabs. The
  // four department entries stay: anyone on a bookmark keeps their screen until
  // they choose to move, which is the same courtesy `/billing` was given when
  // OP Billing replaced it.
  /**
   * "Cash Counter", not "OP Billing".
   *
   * The screen behind it is the Universal Billing workstation: it bills OP
   * services, lab, radiology and account heads from one counter, and the
   * `/op-billing` path is now the only thing about it that still says OP. The
   * route is unchanged — a label is not a reason to break every bookmark.
   */
  page('billing-op', 'billing', 'Cash Counter', '/op-billing', { icon: PaymentsIcon, requires: [FinancialPermissions.FINANCIAL_PAYMENT_PROCESS] }),
  // ADR-081 Phase 1. Gated on the ACCOUNT permission, not the payment one: a
  // cashier processes payments; only a controller reconfigures where revenue
  // posts. Reusing FINANCIAL_PAYMENT_PROCESS here would have handed the chart
  // of accounts to every biller in the hospital.
  page('finance-configuration', 'finance', 'Finance Configuration', '/finance/configuration', {
    requires: [FinancialPermissions.FINANCIAL_ACCOUNT_READ],
  }),
  // ADR-085 Phase 14. Repointed at the IP Billing Workstation, following the
  // same move OP Billing made above: the sidebar leads with the new screen and
  // `/billing/ip` keeps working for anyone on a bookmark. The permission
  // changes with it — from the OP cashier's `financial:payment:process` to
  // `ip:admission:view`, because the workstation reads admission ledgers and
  // the old entry advertised it to a roster that cannot open one.
  page('billing-ip', 'billing', 'IP Billing', '/ip-billing', { icon: PaymentsIcon, requires: [IpAdmissionPermissions.IP_ADMISSION_VIEW],
  }),
  page('billing-lab', 'billing', 'Laboratory Billing', '/billing/laboratory', { icon: BiotechIcon, requires: [FinancialPermissions.FINANCIAL_PAYMENT_PROCESS],
  }),
  page('billing-radiology', 'billing', 'Radiology Billing', '/billing/radiology', { icon: CameraAltIcon, requires: [FinancialPermissions.FINANCIAL_PAYMENT_PROCESS],
  }),
  page('billing-package', 'finance', 'Package Billing', '/billing/packages', { requires: [FinancialPermissions.FINANCIAL_PACKAGE_MANAGE] }),
  page('billing-credit-notes', 'finance', 'Credit Notes', '/billing/refunds', { requires: [FinancialPermissions.FINANCIAL_REFUND_PROCESS] }),
  // ── Finance ────────────────────────────────────────────────────────
  //
  // The accounting half: ledgers, vouchers, the trial balance and the
  // configuration that decides where revenue posts. Kept apart from Billing on
  // purpose — a cashier holds FINANCIAL_PAYMENT_PROCESS and nothing here; a
  // controller holds these and rarely touches a counter.
  /*
    ── THE MAIN COUNTER IS ITS OWN SECTION (desk, 2026-08-28) ────────────────

    "this should not be Finance... keep separate as a Main Counter."

    It sat under Finance for one release and that was the wrong shelf. Finance
    is where the books are CLOSED — ledgers, trial balance, payables — read at
    month end by whoever reconciles them. The Main Counter is the LIVE custody
    of cash: which drawers are open, whose shift is unsettled, who accepts a
    count. Different people, different hour of the day, different question.

    Its own `MAIN_COUNTER` module grant travels with it, so a supervisor who
    oversees counters no longer has to be handed the general ledger to do it.
  */
  /*
    RADIOLOGY IS ONE SCREEN, AND THAT IS THE HONEST ANSWER — the same note
    `WORKSPACE_FLAT_NAV.radiology` already carries. The section exists so the
    counter that runs it has somewhere to be, and it grows a real rail the day
    a radiology module does.
  */
  group('radiology', null, 'Radiology', 'RADIOLOGY', CameraAltIcon),
  page('rad-billing', 'radiology', 'Radiology Billing', '/billing/radiology', { icon: CameraAltIcon, requires: [FinancialPermissions.FINANCIAL_PAYMENT_PROCESS],
  }),

  group('main-counter', null, 'Main Counter', 'MAIN_COUNTER', AccountBalanceIcon),
  page('counter-main', 'main-counter', 'Dashboard', '/counter', { icon: InsightsIcon, requires: [CashManagementPermissions.CASH_SUMMARY_READ] }),
  /*
   * THE COUNTERS THE MAIN COUNTER ALSO WORKS — desk, 2026-08-28: "under Main
   * Counter workstation…. need Cash Counter and IP Billing pages required",
   * ordered "First Cash Counter, IP Billing, Settlement, Reconcilation with
   * dashboard".
   *
   * NEW IDS POINTING AT THE EXISTING ROUTES, not moved entries: `billing-op`
   * and `billing-ip` stay where they are under Billing, because a cashier who
   * works the OP desk must keep reaching them from their own workspace. A
   * registry item has exactly one parent, so appearing in two sections means
   * two rows over one route — the route itself is declared once, in AppRouter.
   *
   * GATED ON WHAT THE PAGES ACTUALLY NEED, not on `CASH_SUMMARY_READ` like
   * their neighbours. Gating them on the section's own permission would put two
   * rows in front of a Main Counter Supervisor that answer 403 the moment they
   * are clicked — a menu that advertises somewhere you cannot go is the same
   * defect as the empty sidebar this section was already fixed for once.
   */
  page('counter-cash-counter', 'main-counter', 'Cash Counter', '/op-billing', {
    requires: [FinancialPermissions.FINANCIAL_PAYMENT_PROCESS],
  }),
  page('counter-ip-billing', 'main-counter', 'IP Billing', '/ip-billing', {
    requires: [IpAdmissionPermissions.IP_ADMISSION_VIEW],
  }),
  page('counter-settlement', 'main-counter', 'Settlement', '/counter/settlement', { icon: AccountBalanceIcon, requires: [CashManagementPermissions.CASH_SUMMARY_READ] }),
  /*
    VERIFICATION sits between Settlement and Reconciliation because that is the
    order the day closes in: every transaction is signed off, then the drawer's
    declaration is accepted, then the books are reconciled.

    GATED ON THE PERMISSION ITS OWN ENDPOINTS CHECK — `cashier:verification:view`
    — and not on `CASH_SUMMARY_READ` like its neighbours. Gating a row on a
    permission the route does not read is how a leaf becomes visible to a role
    that gets a 403 on click, which this codebase has done before.
  */
  page('counter-verification', 'main-counter', 'Verification', '/counter/verification', { icon: ReceiptLongIcon, requires: [CashierPermissions.CASHIER_VERIFICATION_VIEW] }),
  page('counter-reconciliation', 'main-counter', 'Reconciliation', '/counter/reconciliation', { icon: ReceiptLongIcon, requires: [CashManagementPermissions.CASH_SUMMARY_READ] }),

  group('finance', null, 'Finance', 'BILLING_FINANCE', AccountBalanceIcon),
  /*
    ── FINANCE IS SEVEN PAGES, NOT TWELVE LOOSE ROWS (desk, 2026-10-18: "proper sidebar pages and sub tabs looks like
    Odoo") ────────────────────────────────────────────────────────────────

    Odoo's Accounting app is a few menus, each holding the screens that belong together. Finance had one row per
    popular tab and a 30-tab pill strip for the rest. Now each ROW is a page — Receivables, Payables, Accounting,
    Cash & Bank, Banking, Reporting, Setup — and the page shows its own tabs in an Odoo-style bar (`FinanceShell`).
    Which tab belongs to which page is `features/finance/odoo/finance-menu.ts`, read here and by the shell.

    A page row is shown when the user can open ANY of its tabs (`requiresAny`), and opens its landing tab; a user who
    cannot open the landing tab is shown the first one they can, on the same page.

    EVERY TAB ALSO HAS A HIDDEN, SEARCHABLE ENTRY. Taking twelve rows out of the sidebar must not take "Trial Balance"
    or "Day Book" out of Quick Search, so each tab keeps an entry that is not in the tree (HIDDEN) but is found by name
    (`searchable`), carries the tab's own permission, and names its page (`trailVia`) so the sidebar highlights the page
    and the header reads "Finance > Reporting > Day Book" whichever tab is open.
  */
  ...FINANCE_PAGES.map((financePage) =>
    page(financePageRowId(financePage.page), 'finance', financePage.title, financeTabUrl(financePage.landing), {
      requires: [FINANCE_ROUTE_PERMISSION],
      requiresAny: financePageNeeds(financePage),
      icon: FINANCE_PAGE_ICONS[financePage.page],
    }),
  ),
  ...FINANCE_PAGES.flatMap((financePage) =>
    financePage.tabs.map((financeTab) =>
      page(financeTabEntryId(financeTab.tab), 'finance', financeTab.title, financeTabUrl(financeTab.tab), {
        status: 'HIDDEN',
        requires: [...new Set([FINANCE_ROUTE_PERMISSION, financeTab.needs])],
        searchable: true,
        trailVia: financePageRowId(financePage.page),
      }),
    ),
  ),


  // ── Cost Centers (Finance › Cost Centers) ───────────────────────────
  page('cc-home', 'finance', 'Cost Centers', '/finance/cost-centers', { requires: [FinancialPermissions.FINANCIAL_COST_CENTER_READ], icon: AccountBalanceIcon }),
  page('cc-pl', 'finance', 'Cost Center P&L', '/finance/cost-centers?tab=pl', { requires: [FinancialPermissions.FINANCIAL_COST_CENTER_READ], icon: AccountBalanceIcon }),
  page('cc-setup', 'finance', 'Cost Center Setup', '/finance/cost-centers?tab=setup', { requires: [FinancialPermissions.FINANCIAL_COST_CENTER_READ], icon: TuneIcon }),

  // ── Reports & Analytics ────────────────────────────────────────────
  // Each domain-specific report leaf carries its own `moduleCode` (same
  // taxonomy as the top-level sections) IN ADDITION to `requires` — a leaf
  // with no permission gate is otherwise unconditionally visible to any role
  // holding the REPORTS module, regardless of whether that role has
  // anything to do with Clinical/Laboratory/Insurance. Root-caused live:
  // a Pharmacy-only role (REPORTS module, no CLINICAL/LABORATORY/
  // INSURANCE module) still saw "Clinical"/"Laboratory"/"Insurance"
  // report stubs. `rep-executive`/`rep-operational`/`rep-custom` are
  // deliberately left untagged — genuinely cross-cutting, no single
  // owning module.
  group('reports', null, 'Reports & Analytics', 'REPORTS', AssessmentIcon),
  /*
    MOST REPORTS STILL LIVE INSIDE THE WORKSTATIONS THAT PRODUCE THEM
    — 2026-09-04, and that remains right for a report belonging to one desk:
    the counter's own register, the pharmacy's day report, the lab's reports
    queue. A sidebar that points at where the work is done is worth more than
    one naming a module the codebase does not have, and the two leaves at the
    bottom of this section still address those tabs for exactly that reason.

    WHAT CHANGED — 2026-09-06. Three reports now exist that belong to no
    workstation, because each reads ACROSS bounded contexts: registrations
    beside the appointments taken against them, a doctor's new/repeat/follow-up
    mix, and the day's OP and IP money in one place. There is no desk that owns
    those questions, so building them inside one would have meant building them
    three times. They have a route of their own — `/reports` — which is the
    first standalone report route in this application, and these three leaves
    deep-link its tabs.

    EACH LEAF CARRIES `analytics:report:view`. A leaf with no permission gate is
    unconditionally visible to any role holding the REPORTS module, which is
    how a Pharmacy-only role once saw Clinical and Laboratory report stubs.
  */
  /*
    A FOURTH REPORT — 2026-09-07, and the only one with a second route.

    The Front Desk dashboard replaced the session-scoped panel that used to sit
    under New Patient Registration. It lives here because it reads the same
    `/analytics` surface as the three below it, and it ALSO has its own leaf at
    the top of the Front Desk section pointing at `/front-desk/dashboard` — the
    desk asked for both ("create one main Dashboard for front desk itself" and
    "same register using under report & admin module"). Two leaves, two routes,
    ONE component: see `FrontDeskOverviewTab`.

    That is a page reachable two ways, which this file already does deliberately
    for the three billing rows shared with the Billing section — "a workspace
    decides what is OFFERED together, not what exists".
  */
  /*
    TWO PAGES, NOT FOUR LEAVES — 2026-10-23 (desk: "separate OP and IP sub pages in
    that need reports"). The four reports above used to be four sidebar rows, each a
    tab of `/reports`. They are now two rows, one per population: OP Reports (Front Desk
    dashboard, Registration & Appointments, Doctor Consultations, Collection, Service
    Code Report) and IP Reports (Front Desk dashboard, Collection, Service Code Report).
    The tab strip inside the page is how a reader moves between them, and every old
    `/reports?tab=…` link still opens — see `reportTabs.ts`.

    BOTH CARRY `analytics:report:view`, like every report leaf before them: a leaf with
    no gate is visible to any role holding the REPORTS module.
  */
  page('rep-op', 'reports', 'OP Reports', '/reports?scope=op', {
    icon: PeopleIcon,
    requires: [AnalyticsPermissions.ANALYTICS_REPORT_VIEW],
  }),
  page('rep-ip', 'reports', 'IP Reports', '/reports?scope=ip', {
    icon: InsightsIcon,
    requires: [AnalyticsPermissions.ANALYTICS_REPORT_VIEW],
  }),
  /*
    THE MONTHLY STATEMENT — 2026-10-05. The dashboard's OP and IP figures with the
    department and doctor codes, a Print, and the workbook for the government.
    Last in the Reports & Analytics block of reports that read `/analytics`, and
    gated like the rest on `analytics:report:view`; the Print and Excel buttons
    inside it additionally need `analytics:report:export`.
  */
  page('rep-statistics', 'reports', 'Statistics', '/reports?tab=statistics', {
    icon: FactCheckIcon,
    requires: [AnalyticsPermissions.ANALYTICS_REPORT_VIEW],
  }),
  page('rep-counter', 'reports', 'Counter & Collection', '/op-billing?tab=reports', { requires: [FinanceAccountingPermissions.FINANCE_TRIAL_BALANCE_READ] }),
  // `rep-laboratory` was here and is gone: Laboratory is its own top-level
  // workstation now and carries its own `Reports` row, so listing it again
  // here was the same page twice.
  page('rep-pharmacy', 'reports', 'Pharmacy', '/pharmacy-billing/workstation?tab=reports', { requires: [ProcurementPermissions.PROCUREMENT_PO_READ] }),

  // ── Human Resources ───────────────────────────────────────────────
  //
  // First slice (Attendance, Leave, Roster, Credentials/Documents) shipped
  // 2026-09-15 but never got a row here — the `HR` module code and its own
  // top-level section were among the three (Insurance, Materials, HR) this
  // file's header describes stripping on 2026-08-19 for having no real page
  // behind them. There is a real page now: `HrAttendancePage`, one route
  // with self-service (`/me`) and management tabs switched by local
  // component state, not the URL, so — unlike General Stores' tabs below —
  // there is no `?tab=` to deep-link into a specific one yet. One row until
  // that changes.
  //
  // PROVISIONING NOTE (same trap as `general-stores` above): `HR` is
  // currently granted in `role_modules` to SuperAdmin and System
  // Administrator only. Any other role needs it granted there too, or this
  // section stays invisible to them no matter what `hr:*` permissions they
  // hold.
  group('human-resources', null, 'HR', 'HR', BadgeIcon, [HrPermissions.HR_ATTENDANCE_VIEW], '/hr/attendance'),
  // Phase 2A — recruitment (job requisition). Own `requires`, same as
  // General Stores' children above: someone holding ONLY
  // `hr:requisition:view` without `hr:attendance:view` still won't see the
  // HR section at all, since the group-level gate above is unchanged — the
  // same accepted tradeoff General Stores already lives with for its own
  // children (Inward's `PROCUREMENT_GRN_READ` differs from the group's own
  // `STOCK_BATCH_READ`). Revisit only if that combination is actually hit.
  page('hr-recruitment', 'human-resources', 'Recruitment', '/hr/recruitment', {
    icon: PersonSearchIcon,
    requires: [HrPermissions.HR_REQUISITION_VIEW],
  }),
  // Phase 3 — onboarding. Same group-level-gate tradeoff noted above.
  page('hr-onboarding', 'human-resources', 'Onboarding', '/hr/onboarding', {
    icon: AssignmentTurnedInIcon,
    requires: [HrPermissions.HR_ONBOARDING_VIEW],
  }),
  // Phase 4 — training & development. Same group-level-gate tradeoff noted above.
  page('hr-training', 'human-resources', 'Training', '/hr/training', {
    icon: SchoolIcon,
    requires: [HrPermissions.HR_TRAINING_VIEW],
  }),

  // ── Admin ──────────────────────────────────────────────────────────
  /*
    MODIFICATIONS — desk, 2026-10-07: "need new Module called Modifications… one option called Correction… OP and IP. Only the
    approve flow should act in this module… show only who have approve access under Administration → Approver. Even super admin
    and administrator can't see this module."

    NOT A ROLE MODULE. `MODIFICATIONS` is not in `NAV_MODULE_CODES` and no `role_modules` row can grant it: the SuperAdmin and
    System Administrator hold every real code, so a module opened the ordinary way would open for exactly the people it must stay
    shut to. `useEffectiveModuleCodes` adds the code for a user the server says is a selected approver and for nobody else, and
    every reader of module codes (this sidebar, the workspace switcher, global search, the landing route) reads through it.

    BOTH ROWS CARRY THE CODE, the leaf as well as the group: a workspace rail is a flat list of leaves, and a leaf with no gate of
    its own would be offered to anyone for as long as that workspace stayed selected in their browser.
  */
  group('modifications', null, 'Modifications', MODIFICATIONS_MODULE, PublishedWithChangesIcon, undefined, '/modifications/correction'),
  { ...page('modifications-correction', 'modifications', 'Correction', '/modifications/correction', { icon: FactCheckIcon, badgeSource: 'correction-approvals-waiting' }), moduleCode: MODIFICATIONS_MODULE },

  // ── Download ───────────────────────────────────────────────────────────────────────────────────────────────────
  /*
    DOWNLOAD (owner, 2026-10-22: "need new module in sidebar and show only admin permission users … all are excel only with detailed info").

    Excel workbooks of the hospital's records: OP, IP, laboratory, pharmacy and stores, finance, patients, HR, masters and the audit trail.
    The module is opened by the PERMISSION, not by a role module: `DOWNLOAD` is not in `NAV_MODULE_CODES`, and `useEffectiveModuleCodes` adds
    it only for a user whose token carries `data-export:read` (see `core/permissions/download-access.ts`). Every row also names that
    permission, and the server checks it again — and the stricter run/download grants — on every request.

    One row per area, because the catalog is long (37 workbooks): each row opens the same page filtered to that area (`?area=`).
  */
  group('download', null, 'Download', DOWNLOAD_MODULE, FileDownloadIcon, undefined, DOWNLOAD_PATH),
  { ...page('download-all', 'download', 'All downloads', DOWNLOAD_PATH, { icon: FileDownloadIcon, requires: [PlatformPermissions.DATA_EXPORT_READ] }), moduleCode: DOWNLOAD_MODULE },
  ...DOWNLOAD_AREAS.map((area): NavRegistryItem => ({
    ...page(`download-${area.id}`, 'download', area.short, `${DOWNLOAD_PATH}?area=${area.id}`, { icon: DOWNLOAD_AREA_ICON[area.id], requires: [PlatformPermissions.DATA_EXPORT_READ] }),
    moduleCode: DOWNLOAD_MODULE,
  })),
  { ...page('download-files', 'download', 'Ready files', `${DOWNLOAD_PATH}?view=files`, { icon: Inventory2Icon, requires: [PlatformPermissions.DATA_EXPORT_READ] }), moduleCode: DOWNLOAD_MODULE },
  /*
    ONE ROW: DASHBOARD — desk, 2026-09-04: "rename administrator to Admin. when
    click need dashboard page. in dashboard need access for all pages. under
    sidebar just Dashboard under admin".

    This section has now been three shapes in one day, and the last one is the
    right one:

      1. TEN hand-picked screens out of forty-six, one of which ("Sequences")
         pointed at a tab id that does not exist.
      2. EIGHT leaves, one per console group — better, but it duplicated in the
         sidebar the very index the console already draws, so the same eight
         groups were listed twice on screen.
      3. This: one row into the console, which IS the index. The dashboard
         carries the KPI tiles, the audit feed, the search and all eight group
         cards, so every one of the forty-six screens is one click from it.

    A sidebar's job here is to get the administrator to the console, not to
    reproduce it. `?group=` still works for anything that deep-links a group —
    it is simply no longer the sidebar's business.

    THE GROUP CARRIES THE PATH so clicking "Admin" itself opens the console
    rather than only expanding, and so a bare `/administration` resolves in the
    breadcrumb trail. No group-level `requires`: the console gates every screen
    behind it, and an administrator holding only one admin permission must still
    reach the one thing they can do.
  */
  group('administration', null, 'Admin', 'ADMINISTRATION', AdminPanelSettingsIcon, undefined, '/administration'),
  page('admin-dashboard', 'administration', 'Dashboard', '/administration', { icon: InsightsIcon }),
];

export function getChildren(parentId: string | null): NavRegistryItem[] {
  return navigationRegistry.filter((item) => item.parentId === parentId).sort((a, b) => a.order - b.order);
}

export function getTopLevelSections(): NavRegistryItem[] {
  return getChildren(null);
}

/**
 * ALL of `requires` AND (if set) the item's own sidebar-module grant.
 *
 * The module check is a separate dimension from permissions: Role -> Module
 * gates the SECTION, and permissions still gate the leaf underneath it.
 *
 * MOVED HERE FROM `Sidebar.tsx` (2026-09-08), unchanged. It lived in the
 * component because the sidebar was its only reader; the landing resolver below
 * asks the SAME question — "may this user open this row?" — and a second copy
 * beside it is how the sidebar and the screen you land on start disagreeing
 * about what you can see. `Sidebar.tsx` re-exports both so its own test and
 * every existing import are unaffected.
 */
export function hasAccess(item: NavRegistryItem, permissions: string[], moduleCodes: string[]): boolean {
  const permissionsOk = !item.requires || item.requires.every((permission) => permissions.includes(permission));
  const anyOk = !item.requiresAny || item.requiresAny.some((permission) => permissions.includes(permission));
  const moduleOk = !item.moduleCode || moduleCodes.includes(item.moduleCode);
  return permissionsOk && anyOk && moduleOk;
}

/** True if this item, or any descendant, is a real (non-Coming-Soon) leaf the current user can see — decides whether an empty section renders at all. */
export function isVisible(item: NavRegistryItem, permissions: string[], moduleCodes: string[]): boolean {
  if (item.status === 'HIDDEN' || item.status === 'DISABLED') return false;
  if (!hasAccess(item, permissions, moduleCodes)) return false;
  const children = getChildren(item.id);
  if (children.length === 0) return Boolean(item.path);
  return children.some((child) => isVisible(child, permissions, moduleCodes));
}

/**
 * WHICH TOP-LEVEL SECTIONS EACH WORKSPACE OFFERS.
 *
 * A workspace is meant to answer "which part of the hospital am I working in".
 * It did not: only `front-desk` narrowed anything (via `WORKSPACE_FLAT_NAV`),
 * so selecting Pharmacy left the full tree standing — Clinical, Insurance, HR,
 * Administration and the rest — and the switcher looked decorative.
 *
 * Listed by SECTION rather than as a flat rail because these workspaces have
 * real depth: Pharmacy alone carries drug master, stock, procurement and
 * billing. Front Desk keeps its flat rail; it was tuned against live feedback
 * and is not something to re-derive here.
 *
 * ADMINISTRATION IS DELIBERATELY ABSENT, and that absence is the rule: an
 * administrator works across every module, so their workspace narrows nothing.
 * Anything not listed here shows the whole tree, which is the safe direction to
 * fail — a section too many is a longer sidebar, a section too few is a screen
 * somebody cannot reach.
 *
 * GATING IS UNCHANGED. This only decides what is OFFERED; every item still
 * passes the same permission and module checks in `Sidebar.tsx`, so this can
 * never widen what a user may open.
 */
export const WORKSPACE_SECTIONS: Partial<Record<string, string[]>> = {
  // BILLING IS THE COUNTER, FINANCE IS THE LEDGER — no overlap. Billing showed
  // Finance's nine ledger screens for a while; a cashier scrolled past the
  // trial balance to reach a counter they use forty times a day.
  billing: ['billing'],
  // The mother counter, and only it. This desk receives and settles what the
  // billing counters took; it holds no BILLING module and listing `billing`
  // here would offer it rows it cannot open anyway.
  'main-counter': ['main-counter'],
  finance: ['finance'],
  pharmacy: ['pharmacy'],
  /*
   * The store and nothing else. Deliberately NOT listing `pharmacy` alongside
   * it: the two are different custody chains, and a storekeeper offered the
   * pharmacy's rows would be offered a counter they hold no module for anyway.
   */
  'general-stores': ['general-stores'],
  laboratory: ['diagnostics-lab'],
  // The Insurance section and nothing else — its own rail of pre-auth, claims,
  // receipts, receivables, shortfalls, plans and insurers. Each leaf keeps its
  // own `requires`, so this only decides what is offered.
  insurance: ['insurance'],
  /*
   * REPORTS & ANALYTICS — the workspace added 2026-09-08 so this section is
   * reachable by someone who is not an administrator.
   *
   * The section and nothing else. Its leaves already point INTO other desks'
   * screens where the report lives there (`/op-billing?tab=reports`,
   * `/pharmacy-billing/workstation?tab=reports`), so listing those workspaces
   * here as well would offer the whole counter to someone whose job is to read
   * the day — and each leaf still carries its own `requires`, so a reader
   * without the counter's permission simply does not see that row.
   */
  reports: ['reports'],
  // The Modifications section and nothing else — one row, Correction.
  modifications: ['modifications'],
  // The Download section and nothing else — its rows are the areas of the catalog.
  download: ['download'],
  // The Discharge section and nothing else — its rows are the pages of the discharge desk.
  discharge: ['discharge'],
  // The MRD section and nothing else — its rows are the pages of the records room.
  mrd: ['mrd'],
};

/**
 * The top-level sections for a workspace, in registry order.
 *
 * Returns the FULL tree for any workspace with no entry — see the note above
 * on Administration.
 */
export function getSectionsForWorkspace(workspaceId: string): NavRegistryItem[] {
  const allowed = WORKSPACE_SECTIONS[workspaceId];
  const sections = getTopLevelSections();
  if (!allowed) return sections;
  const wanted = new Set(allowed);
  return sections.filter((section) => wanted.has(section.id));
}

/**
 * A flat sidebar for a workspace whose users only ever do a handful of things.
 *
 * Front Desk is the case that forced this: a receptionist was shown the whole
 * hospital — Clinical, Pharmacy, Materials, HR, Insurance, Administration —
 * when their job is six screens. Everything they needed was two levels deep
 * inside groups they had to learn to ignore.
 *
 * This is a VIEW over the same registry, not a second registry and not a
 * deletion: the entries keep their real ids, paths and `requires`, so
 * permission and module gating still apply exactly as before, and every other
 * workspace still sees the full tree. A workspace with no entry here is
 * unaffected.
 */
export const WORKSPACE_FLAT_NAV: Partial<Record<string, Array<{ id: string; icon: SvgIconComponent }>>> = {
  'front-desk': [
    // Icons are supplied here because only GROUPS carry one in the registry —
    // these are `page()` leaves, which normally sit under an icon-bearing
    // parent. Promoted to the top level without one, they rendered as bare
    // text and the sidebar read as a different component from every other
    // workspace's. Same rail, same rows, same affordances — only the list
    // differs.
    /*
      THE DESK'S DASHBOARD, AND IT HAS TO BE ON THIS RAIL — 2026-09-07.

      The row was added to the `front-office` GROUP when the dashboard was
      built, which puts it in the full collapsible tree an administrator sees.
      This rail is a different list: the Front Desk workspace renders THESE ids
      and nothing else, so the screen built for the front desk was visible to
      everyone EXCEPT the front desk. A leaf existing in the registry is not the
      same as a leaf being offered, and this file's own header says so —
      "a workspace decides what is OFFERED together, not what exists".

      FIRST, because a dashboard that is not the first thing in its own rail is
      a dashboard nobody opens, and because the desk asked for "one main
      Dashboard for front desk itself".
    */
    { id: 'fo-dashboard', icon: InsightsIcon },
    { id: 'fo-patient-registration', icon: HowToRegIcon },
    { id: 'fo-appointments', icon: EventAvailableIcon },
    // Admitting sits between booking and billing because that is the order it
    // happens in, not because it is a billing screen. The icon stays as it is:
    // rail icons are learned by position and shape, and re-skinning a row the
    // desk already knows buys nothing.
    { id: 'fo-ip-admission', icon: MeetingRoomIcon },
    // Billing Counter is deliberately ABSENT here. It still exists, is still
    // routed and is still reachable at `/billing/counter` for anyone who holds
    // the permission — this list decides what the Front Desk RAIL offers, not
    // what exists. The desk bills by department (OP, Laboratory, Radiology),
    // and a universal counter sitting above the department screens that do the
    // same act is the thing a clerk opens by mistake.
    { id: 'billing-op', icon: PaymentsIcon },
    // IP Billing is NOT here, removed 2026-08-18 after live feedback. The desk
    // ADMITS (see `fo-ip-admission` above) but does not run the inpatient bill:
    // every money tab on that workstation — charges, charge summary, packages —
    // is gated on `ip:charge:view`/`ip:package:view`, which this role does not
    // hold, so the row opened a screen that answered 403 to everything the
    // operator clicked. A row that leads somewhere the holder cannot use is
    // worse than no row. It stays routed at `/ip-billing` for the roles that do
    // run IP billing.
    { id: 'billing-lab', icon: BiotechIcon },
    { id: 'billing-radiology', icon: CameraAltIcon },
  ],
  /*
   * THE INPATIENT DESK, IN THE ORDER A STAY MOVES (desk, 2026-08-20: "create
   * one IP workstation… move all IP related modules here").
   *
   * Admit, then bill. These are the two IP screens that exist — the same
   * registry entries the Front Office and Billing sections carry, reached
   * through one rail instead of two workspaces.
   *
   * A VIEW, NOT A COPY. Both keep their real ids, paths and `requires`, so a
   * user sees exactly what their permissions already allow: `ip:admission:admit`
   * for admitting, `ip:admission:view` for the workstation. This rail cannot
   * widen anything.
   *
   * THEY ARE NOT REMOVED FROM WHERE THEY WERE. The front desk admits and asked
   * for that row in its own rail on 2026-08-18; Billing keeps IP Billing for the
   * cashier who runs it. A workspace decides what is OFFERED together, not what
   * exists.
   */
  ip: [
    // The dashboard leads: a desk opening the workspace asks "what is the state
    // of the ward" before it asks "what shall I do with one stay".
    { id: 'ip-dashboard', icon: InsightsIcon },
    // FIND, then admit, then bill. Lookup sits second because "which stay is
    // this" is asked far more often than either of the acts below it, and it is
    // the only one of the three a read-only role can reach.
    { id: 'ip-lookup', icon: SearchIcon },
    { id: 'fo-ip-admission', icon: MeetingRoomIcon },
    { id: 'billing-ip', icon: PaymentsIcon },
    { id: 'ip-drug-entries', icon: MedicationIcon },
  ],
  // ONE ITEM, AND THAT IS THE HONEST ANSWER. The Radiology section was five
  // `/coming-soon/*` placeholders (Orders, Scheduling, Reporting, Images, PACS)
  // and was removed with every other placeholder on 2026-08-19. Radiology
  // Billing is the only radiology screen that exists; the workspace exists so
  // the counter that runs it has somewhere to be, and it grows a real rail the
  // day a radiology module does.
  radiology: [{ id: 'billing-radiology', icon: CameraAltIcon }],
};

/**
 * The flat item list for a workspace, in the order given above — deliberately
 * the caller's order, not registry order, because that order is the workflow:
 * register, book, then bill by department.
 *
 * Returns null when the workspace has no flat nav, which means "render the
 * normal tree".
 */
/**
 * Every page under a section, as a flat list, carrying an icon down from the
 * nearest ancestor that has one.
 *
 * Groups themselves are dropped even when they have their own `path`: on a flat
 * rail a parent row sitting immediately above its own children is one row for
 * two ideas, and the operator cannot tell which one they are on.
 */
function flattenSectionPages(item: NavRegistryItem, inheritedIcon?: SvgIconComponent): NavRegistryItem[] {
  const icon = item.icon ?? inheritedIcon;
  const children = getChildren(item.id);
  if (children.length === 0) return item.path ? [{ ...item, icon }] : [];
  return children.flatMap((child) => flattenSectionPages(child, icon));
}

/**
 * The flat item list for a workspace.
 *
 * TWO SOURCES, ONE SHAPE. Front Desk (and Radiology) name their items
 * explicitly in `WORKSPACE_FLAT_NAV`, because the order is a workflow and some
 * items are deliberately left out. Every other scoped workspace DERIVES its
 * rail from `WORKSPACE_SECTIONS`, so it cannot drift from the tree it came from
 * — adding a page to Pharmacy puts it on the Pharmacy rail with no second list
 * to remember.
 *
 * WHY FLAT EVERYWHERE. A collapsible group hides its contents behind a chevron:
 * the Billing workspace showed "Finance" as a closed row, and Pharmacy showed
 * its ten screens nested under a heading that had to be opened first. Desk staff
 * work one workspace all day and want its screens visible, not filed.
 *
 * Returns null for a workspace with no scope — Administration — which keeps the
 * full collapsible tree, because an administrator crosses every module and a
 * flat list of every page in the hospital is not a sidebar.
 */
export function getFlatNavForWorkspace(workspaceId: string): NavRegistryItem[] | null {
  const entries = WORKSPACE_FLAT_NAV[workspaceId];
  if (entries) {
    const byId = new Map(navigationRegistry.map((item) => [item.id, item]));
    const resolved: NavRegistryItem[] = [];
    for (const entry of entries) {
      const item = byId.get(entry.id);
      // The icon is the only thing overridden. Everything else — path,
      // `requires`, status — comes from the registry unchanged, so gating and
      // routing cannot drift from the tree these items also live in.
      if (item) resolved.push({ ...item, icon: item.icon ?? entry.icon });
    }
    return resolved;
  }

  if (!WORKSPACE_SECTIONS[workspaceId]) return null;
  return getSectionsForWorkspace(workspaceId).flatMap((section) => flattenSectionPages(section));
}

/**
 * WHERE `/` SHOULD LAND — desk, 2026-09-08: "instead this blank screen should
 * get dashboard screen of their modules."
 *
 * The home screen was a greeting on an empty page. It had been the Stage F5
 * widget grid until the 2026-08-29/30 desk round removed it, and what was left
 * said the hospital's name and nothing else — the first screen of every shift,
 * carrying no work.
 *
 * THE ANSWER IS A SCREEN THAT ALREADY EXISTS, not a new one. Every scoped
 * workspace opens on a rail whose FIRST row is its own dashboard — Front Desk's
 * `/front-desk/dashboard`, the lab's Dashboard, Pharmacy's hub — each built for
 * the people who work there and already carrying their day's figures. Landing
 * on the first row of the user's own rail is therefore "the dashboard screen of
 * their modules", with nothing invented and nothing to keep in step.
 *
 * IT IS THE FIRST ROW THEY CAN ACTUALLY OPEN, which is not always the first row.
 * `fo-dashboard` requires `analytics:report:view`, and a Front Desk role
 * without that grant would be redirected to a screen that 403s — so this walks
 * the rail in order and returns the first entry `isVisible` accepts, which for
 * that user is New Patient Registration. Same gate as the sidebar, because it
 * is now literally the same function.
 *
 * NULL MEANS "STAY WHERE YOU ARE". A workspace with no scoped rail
 * (Administration keeps the full tree) and a user with nothing visible at all
 * both land here, and the caller shows the greeting rather than redirecting to
 * a guess — a redirect loop on the home route would lock a user out of the
 * application entirely.
 */
export function getLandingPathForWorkspace(
  workspaceId: string,
  permissions: string[],
  moduleCodes: string[],
): string | null {
  const rail = getFlatNavForWorkspace(workspaceId);
  if (!rail) return null;
  const first = rail.find((item) => item.path && isVisible(item, permissions, moduleCodes));
  return first?.path ?? null;
}

function segments(pathname: string): string[] {
  return pathname.split('/').filter(Boolean);
}

/** True if `itemPath` (query included) exactly matches, or — for a plain pathname item — every one of its segments is a prefix of the current pathname's segments (so `/patients` matches the dynamic detail route `/patients/:patientId` too). Exported so `Sidebar.tsx`'s active-state check can reuse the exact same rule `getBreadcrumbTrail()` uses below, instead of approximating it with a bare equality check. */
export function pathsMatch(itemPath: string, currentPathWithSearch: string): boolean {
  if (itemPath === currentPathWithSearch) return true;
  if (itemPath.includes('?')) return false;
  const itemSegments = segments(itemPath);
  const currentSegments = segments(currentPathWithSearch.split('?')[0] ?? '');
  return itemSegments.length > 0 && itemSegments.every((segment, index) => currentSegments[index] === segment);
}

/**
 * Walks parentId ancestry from whichever item's `path` matches the
 * current route, root-first. Among all registry paths that match (e.g.
 * both `/patients` and a hypothetical `/patients/x`), the LONGEST
 * (most specific, by segment count) wins — a dynamic detail route like
 * `/patients/:patientId` has no literal registry entry of its own, so
 * it resolves to its nearest real ancestor (`/patients`, "Patient
 * Search") rather than showing no breadcrumb at all; the page itself
 * can append the real record name via `useBreadcrumbExtra()`.
 */
export function getBreadcrumbTrail(currentPathWithSearch: string): NavRegistryItem[] {
  const candidates = navigationRegistry.filter((item) => item.path && pathsMatch(item.path, currentPathWithSearch));
  // An exact match (e.g. a `?tab=` entry equal to the current URL) always wins
  // over a same-segment-count prefix match (e.g. a bare, query-less ancestor
  // entry) — otherwise a query-string path and a bare pathname path tie on
  // segment count (the query string isn't split into its own segment), and
  // the stable sort silently favors whichever was declared first in the
  // registry, regardless of which one is actually the current page.
  const match = candidates.sort((a, b) => {
    const aExact = a.path === currentPathWithSearch ? 1 : 0;
    const bExact = b.path === currentPathWithSearch ? 1 : 0;
    if (aExact !== bExact) return bExact - aExact;
    const segDiff = segments(b.path ?? '').length - segments(a.path ?? '').length;
    if (segDiff !== 0) return segDiff;
    // Equally-specific exact matches (e.g. a `group()` and its own `page()`
    // leaf both pointing at the same bare module URL, like "Pharmacy" and
    // its "Dashboard" leaf both at `/pharmacy`) — the leaf is always
    // declared after its group, so preferring later declaration order picks
    // the real destination page over the generic group node.
    return navigationRegistry.indexOf(b) - navigationRegistry.indexOf(a);
  })[0];
  if (!match) return [];
  const trail: NavRegistryItem[] = [];
  // A sub-tab entry is not a sidebar row; it names the row it belongs to, and the trail climbs from THAT row so the
  // sidebar highlights the page and the header reads "Finance > Reporting > Day Book".
  const via = match.trailVia ? navigationRegistry.find((item) => item.id === match.trailVia) : undefined;
  if (via) trail.push(match);
  let current: NavRegistryItem | undefined = via ?? match;
  while (current) {
    trail.unshift(current);
    current = current.parentId ? navigationRegistry.find((item) => item.id === current!.parentId) : undefined;
  }
  return trail;
}

/** Permission- and module-filtered the same way the sidebar is — `permissions`/`moduleCodes` are the current user's flat claim arrays (`useAuthStore()`). A leaf's own group may be gated by a module the leaf itself doesn't repeat, so this also walks up to the nearest ancestor with a `moduleCode` set. */
export function searchNavigation(query: string, permissions: string[], moduleCodes: string[]): NavRegistryItem[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  const gatingModuleOk = (item: NavRegistryItem): boolean => {
    let current: NavRegistryItem | undefined = item;
    while (current) {
      if (current.moduleCode) return moduleCodes.includes(current.moduleCode);
      current = current.parentId ? navigationRegistry.find((candidate) => candidate.id === current!.parentId) : undefined;
    }
    return true;
  };
  return navigationRegistry.filter(
    (item) =>
      item.path &&
      item.searchable !== false &&
      // A HIDDEN item is searchable only if it says so: a sub-tab of a sidebar page is not a row in the tree but is
      // still somewhere a person types a name to reach.
      (item.status === 'ACTIVE' || (item.status === 'HIDDEN' && item.searchable === true)) &&
      item.label.toLowerCase().includes(q) &&
      (!item.requires || item.requires.every((permission) => permissions.includes(permission))) &&
      (!item.requiresAny || item.requiresAny.some((permission) => permissions.includes(permission))) &&
      gatingModuleOk(item),
  );
}

/** Extension point for future navigation analytics — intentionally a no-op today (see plan's "Deferred, not in this phase"). */
export function trackNavigation(_event: { itemId: string; action: 'click' | 'favorite' | 'search' }): void {
  // no-op — wiring this to a real backend endpoint is a future, separate change.
}
